import { createServerFn } from "@tanstack/react-start"
import { createClient } from "@supabase/supabase-js"
import type { Database } from "@/integrations/supabase/types"

export type SentimentPoint = {
  at: string
  longPct: number
  shortPct: number
  longLots: number | null
  shortLots: number | null
  longPositions: number | null
  shortPositions: number | null
  longAvg: number | null
  shortAvg: number | null
}

export type RetailSentimentDTO = { latest: SentimentPoint | null; history: SentimentPoint[]; error?: string }

export const getGoldRetailSentiment = createServerFn({ method: "GET" }).handler(async (): Promise<RetailSentimentDTO> => {
  try {
    const url = process.env["SUPABASE_URL"]!
    const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!
    const sb = createClient<Database>(url, key, {
      auth: { persistSession: false },
      global: {
        fetch: (input, init) => {
          const h = new Headers(init?.headers)
          if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization")
          h.set("apikey", key)
          return fetch(input, { ...init, headers: h })
        },
      },
    })
    const since = new Date(Date.now() - 30 * 86400_000).toISOString()
    const { data, error } = await sb
      .from("retail_sentiment_snapshots")
      .select("captured_at,long_pct,short_pct,long_lots,short_lots,long_positions,short_positions,long_avg_price,short_avg_price")
      .gte("captured_at", since)
      .order("captured_at", { ascending: true })
      .limit(2000)
    if (error) throw error
    // Self refresh: capture a new snapshot when the latest is older than 30 minutes and the market is open.
    const lastAt = data?.length ? Date.parse(data[data.length - 1].captured_at) : 0
    const now = new Date()
    const d = now.getUTCDay(), h = now.getUTCHours()
    const closed = d === 6 || (d === 5 && h >= 21) || (d === 0 && h < 22)
    if (!closed && Date.now() - lastAt > 30 * 60_000) {
      try {
        const { captureRetailSentiment } = await import("@/server/retail-sentiment.server")
        const row = await captureRetailSentiment()
        data?.push({ captured_at: new Date().toISOString(), ...row } as never)
      } catch (e) {
        console.error("[retail-sentiment] refresh failed", e)
      }
    }
    const n = (v: unknown) => {
      if (v == null || v === "") return null
      const x = Number(v)
      return Number.isFinite(x) && x !== 0 ? x : null
    }
    const history: SentimentPoint[] = (data ?? []).map((r) => ({
      at: r.captured_at,
      longPct: Number(r.long_pct),
      shortPct: Number(r.short_pct),
      longLots: n(r.long_lots),
      shortLots: n(r.short_lots),
      longPositions: n(r.long_positions),
      shortPositions: n(r.short_positions),
      longAvg: n(r.long_avg_price),
      shortAvg: n(r.short_avg_price),
    }))
    return { latest: history.at(-1) ?? null, history }
  } catch (e) {
    console.error("[retail-sentiment] read failed", e)
    return { latest: null, history: [], error: "Sentiment feed temporarily unavailable." }
  }
})
